import {Swiper, SwiperProps, SwiperSlide} from 'swiper/react';
import {Autoplay, Navigation, Pagination} from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';

import {Box, SxProps} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronLeft} from '@jobseeker/lotus/icons';
import {useState} from 'react';

interface CarouselItemProps {
  carouselItems: Array<JSX.Element>;
  swiperProps?: SwiperProps;
  fullFrame?: boolean;
  slidesPerView?: number;
  singular?: boolean;
  hideNavigationOnMobile?: boolean;
  breakpointOverrides?: Record<
    string,
    {slidesPerView: number; spaceBetween: number}
  >;
  whatFor?: string;
  next?: string;
  previous?: string;
  showPagination?: boolean;
}

interface NavigationControlProps {
  enableInFrame: boolean;
  hideOnMobile: boolean;
  isStart: boolean;
  isEnd: boolean;
  whatFor?: string;
}

const navButtonStyles: SxProps = {
  width: 20,
  height: 20,
  position: 'relative',
  display: 'flex',
  boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
  backgroundColor: 'white',
  borderRadius: 4,
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  opacity: 100,
  pointerEvents: 'auto',
  '&.disabled': {
    opacity: 0.5,
    pointerEvents: 'none',
  },
};

const NavigationControl = ({
  enableInFrame,
  hideOnMobile,
  isStart,
  isEnd,
  whatFor = '',
}: NavigationControlProps) => {
  if (hideOnMobile) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        right: {xxs: 8, md: enableInFrame ? 62 : 24},
        top: {
          xxs: 20,
          md: enableInFrame ? 33 : whatFor === 'video-jd' ? 34 : 20,
        },
        transform: 'translateY(-50%)',
        display: {md: 'flex', xs: 'none'},
        gap: 1,
      }}
    >
      <Box
        className={`custom-prev ${isStart ? 'disabled' : ''}`}
        sx={navButtonStyles}
      >
        <Image
          src={ChevronLeft}
          alt="previous"
          width={15}
          height={15}
          style={{
            transform: 'rotate(-180deg)',
          }}
        />
      </Box>

      <Box
        className={`custom-next ${isEnd ? 'disabled' : ''}`}
        sx={navButtonStyles}
      >
        <Image src={ChevronLeft} alt="next" width={15} height={15} />
      </Box>
    </Box>
  );
};

const Carousel = ({
  carouselItems,
  slidesPerView,
  swiperProps,
  fullFrame,
  singular = false,
  hideNavigationOnMobile = false,
  breakpointOverrides,
  whatFor = '',
  next = '',
  previous = '',
  showPagination = true,
}: CarouselItemProps) => {
  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const defaultBreakpoints = {
    360: {
      slidesPerView: 1.1,
      spaceBetween: 16,
    },
    480: {
      slidesPerView: 1.5,
      spaceBetween: 18,
    },
    768: {
      slidesPerView: 2.5,
      spaceBetween: 18,
    },
    1024: {
      slidesPerView: fullFrame ? 1 : 3,
      spaceBetween: 18,
    },
    1280: {
      slidesPerView: fullFrame ? 1 : 3,
      spaceBetween: 18,
    },
    1440: {
      slidesPerView: fullFrame ? 1 : 3,
      spaceBetween: 14,
    },
    1550: {
      slidesPerView: fullFrame ? 1 : 3,
      spaceBetween: 18,
    },
  };

  const breakpoints = {...defaultBreakpoints, ...breakpointOverrides};

  const handleSlideChange = (swiper: any) => {
    const {activeIndex, slides} = swiper;
    const totalSlides = slides.length;
    const visibleSlides =
      slidesPerView ?? breakpoints[1550]?.slidesPerView ?? 3;

    setIsStart(activeIndex <= 0);
    setIsEnd(activeIndex >= totalSlides - Math.ceil(visibleSlides) + 1);
  };

  return (
    <>
      <NavigationControl
        key={`nav_carousel_${fullFrame}`}
        enableInFrame={!!fullFrame}
        hideOnMobile={hideNavigationOnMobile}
        isStart={isStart}
        isEnd={isEnd}
        whatFor={whatFor}
      />
      <Swiper
        slidesPerView={slidesPerView ?? 3}
        spaceBetween={14}
        navigation={
          singular
            ? true
            : {
                nextEl: next || '.custom-next',
                prevEl: previous || '.custom-prev',
              }
        }
        modules={singular && !showPagination ? [Autoplay, Navigation] : singular ? [Pagination, Autoplay, Navigation] : [Navigation]}
        breakpoints={breakpoints}
        autoHeight={true}
        onSlideChange={handleSlideChange}
        onSwiper={swiper => handleSlideChange(swiper)} // Initial check
        {...swiperProps}
      >
        {carouselItems?.map((item, index) => (
          <SwiperSlide key={index}>{item}</SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default Carousel;
